
import React, { useState } from 'react';
import { cn } from '@/lib/utils';
import { LeadType, LeadStatus } from './types/LeadTypes';
import { getLeadTypeIcon } from './LeadUtils';
import { useIsMobile } from "@/hooks/use-mobile";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

export const getStatusColor = (status: LeadStatus): string => {
  switch (status) {
    case 'Call Missed':
      return "text-red-700";
    default:
      return "text-black";
  }
};

export const getStatusIcon = (status: LeadStatus): string => {
  switch (status) {
    case 'Call Answered':
      return "✓";
    case 'Call Missed':
      return "✗";
    case 'Call Returned':
      return "↩";
    case 'Form Received':
      return "📝";
    case 'Form Replied':
      return "📨";
    default:
      return "";
  }
};

interface StatusSelectProps {
  status: LeadStatus;
  onChange: (value: LeadStatus) => void;
  leadType?: LeadType;
}

const StatusSelect: React.FC<StatusSelectProps> = ({
  status,
  onChange,
  leadType = 'Call' // Default to Call if not specified
}) => {
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);
  
  // Filter statuses based on lead type
  const getFilteredStatusOptions = () => {
    if (leadType === 'Call') {
      return ['Call Answered', 'Call Missed', 'Call Returned'];
    } else {
      return ['Form Received', 'Form Replied'];
    }
  };

  const filteredOptions = getFilteredStatusOptions();

  const handleSelect = (option: LeadStatus) => {
    onChange(option);
    setOpen(false); // Close popover after selection
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className={cn(
            "flex items-center gap-1.5 w-full h-8 px-3 py-1.5 text-left bg-white rounded-md focus:outline-none",
            isMobile 
              ? "border border-gray-200" 
              : "border border-transparent hover:border-gray-200 transition-colors"
          )}
        >
          <span className={cn("flex items-center", getStatusColor(status))}>
            {getLeadTypeIcon(leadType, status)}
            <span className="ml-1 text-sm">{status}</span>
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent 
        className="p-0 w-[220px] border border-gray-200 shadow-lg z-50" 
        sideOffset={5} 
        align="start"
      >
        <div className="py-0.5 max-h-60 overflow-auto">
          {filteredOptions.map((option) => (
            <div 
              key={option}
              className="flex items-center px-3 py-1.5 cursor-pointer hover:bg-gray-50"
              onClick={() => handleSelect(option as LeadStatus)}
            >
              <span className={cn("flex items-center", getStatusColor(option as LeadStatus))}>
                {getLeadTypeIcon(leadType, option as LeadStatus)}
                <span className="ml-1 text-sm">{option}</span>
              </span>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default StatusSelect;
